import {
  Group,
  Text,
  Badge,
  Avatar,
  Box,
  Button,
  Menu,
  ActionIcon,
} from '@mantine/core';
import {
  MessageCircle,
  MoreVertical,
  Edit3,
  XCircle,
  RefreshCw,
} from 'react-feather';
import { formatDate, formatTime } from '../../../utils/formatters';
import { getAvatarColor } from '../../../utils/avatarHelpers';
import { HUDDLE_TYPE_BADGE } from './helpers';
import type { CoachHuddle } from '../../../types';

export interface HuddleRowProps {
  huddle: CoachHuddle;
  onClick: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

export function HuddleRow({ huddle, onClick, onEdit, onCancel }: HuddleRowProps) {
  const badge = HUDDLE_TYPE_BADGE[huddle.huddle_type] || { label: 'CHECK IN', color: 'blue' };
  const initials = (huddle.participant_name || '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const participantAvatarColor = getAvatarColor(huddle.participant_user_id || huddle.participant_id);

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      style={{
        backgroundColor: 'rgb(240, 244, 248)',
        padding: '8px 20px',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <Group gap="sm" wrap="nowrap">
        <Box
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'rgb(255, 255, 255)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MessageCircle size={14} color="rgb(92, 122, 153)" />
        </Box>
        <Badge size="xs" variant="light" color={badge.color} tt="uppercase" style={{ letterSpacing: '0.3px' }}>
          {badge.label}
        </Badge>
        <Avatar
          size={32}
          radius="xl"
          styles={{
            root: { backgroundColor: participantAvatarColor },
            placeholder: { color: '#FFFFFF', backgroundColor: participantAvatarColor },
          }}
        >
          {initials}
        </Avatar>
        <div>
          <Text fw={500} size="13px">
            {huddle.participant_name}
          </Text>
          {huddle.participant_title && (
            <Text size="10px" c="dimmed">
              {huddle.participant_title}
            </Text>
          )}
        </div>
      </Group>

      <Group gap="md" wrap="nowrap">
        <div style={{ textAlign: 'right' }}>
          <Text size="13px" fw={500}>
            {formatDate(huddle.scheduled_start)}
          </Text>
          <Text size="10px" c="dimmed">
            {formatTime(huddle.scheduled_start)} &middot; {huddle.duration_minutes} min
          </Text>
        </div>
        <Button
          variant="subtle"
          size="xs"
          color="gray"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          View Notes
        </Button>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={32}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<Edit3 size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={<RefreshCw size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              Reschedule
            </Menu.Item>
            <Menu.Item
              leftSection={<XCircle size={14} />}
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
            >
              Cancel
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
